// client/src/components/pages/LexiStar/Approvals/DriverPhotos.tsx
//
// The driver's photo evidence on a deviation (ST-00188; WhatsApp runbook §5.8),
// shown in the approvals page's "Driver's reply" card: a thumbnail per stored
// photo (fetched through GET /api/lexistar/media with the session and shown
// from a blob: URL), click to enlarge in a lightbox (Esc / the close button /
// the backdrop close it; arrow keys or the buttons step through several), and
// "Photo unavailable" for a photo that cannot be shown — a ref that is not
// s3:// (the connectors could not download it from the provider) or one the
// server would not serve (gone, another organisation's, store down).
//
// Blob URLs are created once per set of refs and revoked when the card goes
// (the page remounts it per deviation). Keys pressed inside the lightbox never
// reach the page's own shortcuts (Esc cancelling a decision, A/R/E/J/K).

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { fetchLexiStarMedia, isStoredMediaRef } from '../../../../services/lexistarMedia';
import type { DriverMediaLike } from '../../../../types/lexistar-media';

export type PhotoState = { status: 'loading' } | { status: 'ready'; url: string } | { status: 'error' };

const LOADING: PhotoState = { status: 'loading' };

/** blob: URLs of the stored photos: fetched once per set of refs, revoked on unmount. */
export function usePhotoUrls(refs: readonly string[]): Readonly<Record<string, PhotoState>> {
  const key = [...new Set(refs)].join('\n');
  const [states, setStates] = useState<Record<string, PhotoState>>({});
  useEffect(() => {
    const list = key ? key.split('\n') : [];
    const controller = new AbortController();
    const created: string[] = [];
    let alive = true;
    const load = (ref: string) =>
      fetchLexiStarMedia(ref, { signal: controller.signal })
        .then((blob) => {
          if (!alive) return;
          const url = URL.createObjectURL(blob);
          created.push(url);
          setStates((s) => ({ ...s, [ref]: { status: 'ready', url } }));
        })
        .catch(() => {
          if (alive) setStates((s) => ({ ...s, [ref]: { status: 'error' } }));
        });
    setStates(Object.fromEntries(list.map((ref) => [ref, LOADING])));
    list.forEach(load);
    return () => {
      alive = false;
      controller.abort();
      for (const url of created) URL.revokeObjectURL(url);
    };
  }, [key]);
  return states;
}

/** 183 KB, 1.2 MB (null when unknown). */
export function fmtBytes(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const NOT_STORED_HINT =
  'The photo was not saved from the messaging provider, so it cannot be shown. Ask the driver to send it again if you need it.';
const FAILED_HINT = 'The photo could not be loaded (it is gone, not yours to see, or the photo store is down).';

function Unavailable({ hint }: { hint: string }) {
  return (
    <span className="text-xs text-slate-500" title={hint}>
      Photo unavailable
    </span>
  );
}

interface Viewable {
  /** Index in the `media` list. */
  index: number;
  url: string;
  bytes: number | null;
}

interface LightboxProps {
  items: readonly Viewable[];
  /** Position in `items`. */
  position: number;
  subject?: string;
  onStep: (delta: 1 | -1) => void;
  onClose: () => void;
}

function Lightbox({ items, position, subject, onStep, onClose }: LightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const item = items[position];
  const many = items.length > 1;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  if (!item) return null;
  const size = fmtBytes(item.bytes);
  const title = `Driver's photo ${position + 1} of ${items.length}${subject ? ` · ${subject}` : ''}`;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Modal: the page's document-level shortcuts never see these keys.
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (many && e.key === 'ArrowRight') {
      e.preventDefault();
      onStep(1);
    } else if (many && e.key === 'ArrowLeft') {
      e.preventDefault();
      onStep(-1);
    } else if (e.key === 'Tab') {
      // Keep focus inside the dialog.
      const buttons = Array.from(dialogRef.current?.querySelectorAll('button') ?? []);
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onBackdrop = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onBackdrop}
      data-testid="driver-photo-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
        className="relative flex max-h-full max-w-full flex-col items-center gap-3"
      >
        <img
          src={item.url}
          alt={`Driver evidence ${position + 1} of ${items.length}${subject ? ` · ${subject}` : ''}`}
          className="max-h-[80vh] max-w-[92vw] rounded-lg bg-white object-contain shadow-2xl"
        />
        <div className="flex items-center gap-3 text-sm text-white">
          {many && (
            <button
              type="button"
              onClick={() => onStep(-1)}
              aria-label="Previous photo"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <FaChevronLeft size={14} />
            </button>
          )}
          <span className="tabular-nums">
            Photo {position + 1} of {items.length}
            {size && ` · ${size}`}
          </span>
          {many && (
            <button
              type="button"
              onClick={() => onStep(1)}
              aria-label="Next photo"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <FaChevronRight size={14} />
            </button>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close photo"
          className="absolute -right-3 -top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <FaTimes size={14} />
        </button>
      </div>
    </div>,
    document.body
  );
}

export interface DriverPhotosProps {
  media: readonly DriverMediaLike[];
  /** Whose photos (the truck's registration), for the alt text and the lightbox title. */
  subject?: string;
}

export default function DriverPhotos({ media, subject }: DriverPhotosProps) {
  const storedRefs = useMemo(() => media.map((m) => m.ref).filter(isStoredMediaRef), [media]);
  const states = usePhotoUrls(storedRefs);
  /** The open photo, by its index in `media` (null: the lightbox is closed). */
  const [open, setOpen] = useState<number | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);

  const viewable: Viewable[] = [];
  media.forEach((m, index) => {
    const st = isStoredMediaRef(m.ref) ? states[m.ref] : undefined;
    if (st?.status === 'ready') viewable.push({ index, url: st.url, bytes: m.bytes ?? null });
  });
  const position = open === null ? -1 : viewable.findIndex((v) => v.index === open);

  if (media.length === 0) return null;

  const close = () => {
    setOpen(null);
    opener.current?.focus();
  };
  const step = (delta: 1 | -1) => {
    if (position < 0 || viewable.length === 0) return;
    const next = viewable[(position + delta + viewable.length) % viewable.length];
    if (next) setOpen(next.index);
  };

  return (
    <>
      <ul className="flex flex-wrap items-center gap-2 pt-1" aria-label="Driver's photos">
        {media.map((m, index) => {
          const n = index + 1;
          const key = `${index}:${m.ref}`;
          if (!isStoredMediaRef(m.ref)) {
            return (
              <li key={key}>
                <Unavailable hint={NOT_STORED_HINT} />
              </li>
            );
          }
          const st = states[m.ref] ?? LOADING;
          if (st.status === 'error') {
            return (
              <li key={key}>
                <Unavailable hint={FAILED_HINT} />
              </li>
            );
          }
          if (st.status === 'loading') {
            return (
              <li key={key}>
                <span
                  role="status"
                  aria-label={`Loading photo ${n}`}
                  className="block h-20 w-20 animate-pulse rounded-lg bg-slate-100"
                />
              </li>
            );
          }
          return (
            <li key={key}>
              <button
                type="button"
                onClick={(e) => {
                  opener.current = e.currentTarget;
                  setOpen(index);
                }}
                aria-label={`Open photo ${n} of ${media.length}`}
                className="block h-20 w-20 overflow-hidden rounded-lg border border-slate-200 hover:ring-2 hover:ring-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <img
                  src={st.url}
                  alt={`Driver evidence ${n}${subject ? ` · ${subject}` : ''}`}
                  className="h-full w-full object-cover"
                />
              </button>
            </li>
          );
        })}
      </ul>
      {position >= 0 && (
        <Lightbox items={viewable} position={position} subject={subject} onStep={step} onClose={close} />
      )}
    </>
  );
}
